import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ApiError, api, type CourseDiscount } from '../lib/api';
import { refreshLennyStatus, startSignIn } from '../lib/identity';
import { useIdentity } from '../lib/use-identity';

export function CourseDiscountPage() {
  const identity = useIdentity();
  const [copied, setCopied] = useState(false);
  const reauthStarted = useRef(false);
  const discountAuthAttempted =
    new URLSearchParams(window.location.search).get('discountAuth') === '1';

  const publicDiscountQuery = useQuery({
    queryKey: ['course-discount', 'public'],
    queryFn: () => api.getPublicCourseDiscount(),
    retry: false,
  });

  const claimQuery = useQuery({
    queryKey: ['course-discount', identity?.email],
    queryFn: () => api.getCourseDiscount(),
    enabled: Boolean(identity),
    retry: false,
  });

  const claim = claimQuery.data?.claim ?? null;
  const publicClaim = publicDiscountQuery.data?.claim ?? null;
  const needsDiscountReauth =
    claimQuery.error instanceof ApiError &&
    claimQuery.error.status === 403 &&
    claimQuery.error.message === 'course_discount_reauth_required';
  const notEligible =
    claimQuery.error instanceof ApiError &&
    claimQuery.error.status === 403 &&
    (claimQuery.error.message === 'coupon_not_available' ||
      (needsDiscountReauth && discountAuthAttempted));
  const courseScopeChecked =
    Boolean(claim) ||
    (claimQuery.error instanceof ApiError &&
      claimQuery.error.message === 'coupon_not_available');
  const loadingExisting = Boolean(identity) && claimQuery.isLoading;
  const error =
    claimQuery.error instanceof ApiError && !notEligible && !needsDiscountReauth
      ? claimQuery.error.message
      : null;

  useEffect(() => {
    if (!needsDiscountReauth || discountAuthAttempted || reauthStarted.current) return;
    reauthStarted.current = true;

    // Preserve a one-time marker in returnTo. If Lenny Access completes but
    // still does not grant the course entitlement, the marker prevents an
    // automatic redirect loop and the page falls back to the public offer.
    const returnUrl = new URL(window.location.href);
    returnUrl.searchParams.set('discountAuth', '1');
    window.history.replaceState(
      {},
      '',
      `${returnUrl.pathname}${returnUrl.search}${returnUrl.hash}`,
    );
    refreshLennyStatus('course-discount');
  }, [discountAuthAttempted, needsDiscountReauth]);

  useEffect(() => {
    if (!discountAuthAttempted || !courseScopeChecked) return;
    const cleanUrl = new URL(window.location.href);
    cleanUrl.searchParams.delete('discountAuth');
    window.history.replaceState({}, '', `${cleanUrl.pathname}${cleanUrl.search}${cleanUrl.hash}`);
  }, [courseScopeChecked, discountAuthAttempted]);

  async function copyCode(code: string): Promise<void> {
    await navigator.clipboard.writeText(code);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1600);
  }

  return (
    <div className="container-page py-10 sm:py-14">
      <div className="mx-auto max-w-4xl">
        <div className="grid gap-8 lg:grid-cols-[1fr,320px] lg:items-start">
          <section>
            <p className="text-xs font-semibold uppercase tracking-wider text-ink-subtle">
              Course discount
            </p>
            <h1 className="mt-3 text-3xl font-semibold tracking-tight sm:text-4xl">
              Get your Become an AI Native Builder discount
            </h1>

            <div className="mt-8 rounded-2xl border border-line bg-surface p-5 shadow-card sm:p-6">
              {!identity ? (
                publicDiscountQuery.isLoading ? (
                  <LoadingState />
                ) : publicClaim ? (
                  <SignedOutState
                    claim={publicClaim}
                    copied={copied}
                    onCopy={() => copyCode(publicClaim.code)}
                  />
                ) : (
                  <ErrorState name={null} error="Could not load the public discount." />
                )
              ) : loadingExisting || (needsDiscountReauth && !discountAuthAttempted) ? (
                <LoadingState />
              ) : claim ? (
                <ClaimedState claim={claim} copied={copied} onCopy={() => copyCode(claim.code)} />
              ) : notEligible && publicClaim ? (
                <CheckedPublicState
                  claim={publicClaim}
                  copied={copied}
                  onCopy={() => copyCode(publicClaim.code)}
                />
              ) : notEligible ? (
                <LoadingState />
              ) : (
                <ErrorState name={identity.name} error={error} />
              )}
            </div>
          </section>

          <aside className="rounded-2xl border border-line bg-canvas p-5">
            <h2 className="text-sm font-semibold tracking-tight">How it works</h2>
            <ol className="mt-4 space-y-4">
              <ClaimStep
                index={1}
                title="Free readers can start now"
                body={`Use the public discount link for ${formatDollars(publicClaim?.discountAmountCents ?? 30000)} off.`}
              />
              <ClaimStep
                index={2}
                title="Annual members get more"
                body="Annual paid members get $600 off. Insiders get $1,000 off after signing in."
              />
              <ClaimStep
                index={3}
                title="Complete enrollment"
                body="Confirm the discounted price at checkout before paying."
              />
            </ol>
            <p className="mt-5 border-t border-line pt-4 text-xs leading-5 text-ink-subtle">
              Your Lenny membership is managed separately. Using this discount will not start,
              upgrade, downgrade, or renew a membership.
            </p>
          </aside>
        </div>
      </div>
    </div>
  );
}

function SignedOutState({
  claim,
  copied,
  onCopy,
}: {
  claim: CourseDiscount;
  copied: boolean;
  onCopy: () => void;
}) {
  return (
    <div>
      <h2 className="text-lg font-semibold tracking-tight">Start with $300 off</h2>
      <p className="mt-2 text-sm text-ink-muted">
        This discount is available without signing in. If you are an annual Lenny member, sign in
        below to check whether you qualify for $600 or $1,000 off instead.
      </p>
      <DiscountAction claim={claim} copied={copied} onCopy={onCopy} />
      <div className="mt-5 rounded-xl border border-line bg-canvas p-4">
        <h3 className="text-sm font-semibold tracking-tight">Paid subscriber?</h3>
        <p className="mt-1 text-xs leading-5 text-ink-muted">
          Sign in with Lenny Access to check for the $600 annual member discount or the $1,000
          insider discount. Free readers do not need to sign in.
        </p>
        <button
          type="button"
          onClick={() => startSignIn('course-discount')}
          className="btn-secondary mt-3"
        >
          Check annual member discount
        </button>
      </div>
    </div>
  );
}

function LoadingState() {
  return (
    <div className="space-y-3">
      <div className="h-5 w-56 animate-pulse rounded bg-canvas" />
      <div className="h-4 w-full max-w-md animate-pulse rounded bg-canvas" />
      <div className="h-11 w-44 animate-pulse rounded-full bg-canvas" />
    </div>
  );
}

function ErrorState({
  name,
  error,
}: {
  name: string | null;
  error: string | null;
}) {
  return (
    <div>
      <h2 className="text-lg font-semibold tracking-tight">Could not load code</h2>
      <p className="mt-2 text-sm text-ink-muted">
        {name ? `Signed in as ${name}, but we could not load a discount code.` : 'We could not load a discount code.'}
      </p>
      {error ? <p className="mt-3 text-sm font-medium text-red-700">{error}</p> : null}
    </div>
  );
}

function ClaimedState({
  claim,
  copied,
  onCopy,
}: {
  claim: CourseDiscount;
  copied: boolean;
  onCopy: () => void;
}) {
  return (
    <div>
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h2 className="text-lg font-semibold tracking-tight">Your member discount</h2>
          <p className="mt-2 text-sm text-ink-muted">
            This discount is for your Lenny{' '}
            {claim.tier === 'insider' ? 'Insider' : 'annual member'} status and can be used for{' '}
            {claim.productName}.
          </p>
        </div>
        <span className="chip bg-canvas text-ink-muted">{claim.tier}</span>
      </div>
      <DiscountAction claim={claim} copied={copied} onCopy={onCopy} />
    </div>
  );
}

function DiscountAction({
  claim,
  copied,
  onCopy,
}: {
  claim: CourseDiscount;
  copied: boolean;
  onCopy: () => void;
}) {
  return (
    <div className="mt-5 flex flex-col gap-3 rounded-xl border border-line bg-canvas p-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <p className="text-xs font-medium uppercase tracking-wider text-ink-subtle">
            {formatDollars(claim.discountAmountCents)} off
          </p>
          <code className="mt-1 block break-all text-xl font-semibold tracking-wide text-ink">
            {claim.code}
          </code>
        </div>
        <button type="button" onClick={onCopy} className="btn-secondary shrink-0">
          {copied ? 'Copied' : 'Copy code'}
        </button>
      </div>
      <a
        href={claim.checkoutUrl}
        className="btn-accent justify-center"
        target="_blank"
        rel="noreferrer"
      >
        Go to checkout
      </a>
    </div>
  );
}

function CheckedPublicState({
  claim,
  copied,
  onCopy,
}: {
  claim: CourseDiscount;
  copied: boolean;
  onCopy: () => void;
}) {
  return (
    <div>
      <h2 className="text-lg font-semibold tracking-tight">Use this $300 discount</h2>
      <p className="mt-2 text-sm text-ink-muted">
        We checked your Lenny membership. The annual member discount isn't available for this
        account, but you can still use this public discount for {claim.productName} or{' '}
        <a
          href="https://www.lennysnewsletter.com/subscribe"
          className="font-medium text-ink underline underline-offset-2"
          target="_blank"
          rel="noreferrer"
        >
          upgrade to annual here
        </a>
        .
      </p>
      <DiscountAction claim={claim} copied={copied} onCopy={onCopy} />
    </div>
  );
}

function formatDollars(cents: number): string {
  return `$${Math.round(cents / 100).toLocaleString('en-US')}`;
}

function ClaimStep({
  index,
  title,
  body,
}: {
  index: number;
  title: string;
  body: string;
}) {
  return (
    <li className="flex gap-3">
      <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-surface text-xs font-semibold text-ink">
        {index}
      </span>
      <div>
        <h3 className="text-sm font-semibold tracking-tight">{title}</h3>
        <p className="mt-1 text-xs leading-5 text-ink-muted">{body}</p>
      </div>
    </li>
  );
}
